<template>
  <SideBar
    :visible="isCartSidebarOpen"
    :title="$t('Cart')"
    data-testid="cartSidebar"
    @close="toggleCartSidebar"
  >
    <template #bar>
      <div class="flex justify-between items-start px-2 py-4 lg:px-4">
        <div class="flex flex-col gap-1">
          <h2 class="font-semibold text-lg flex items-center gap-2">
              <span
                v-if="isCartFrozen"
                  class="iconify fa-pro-solid--snowflake text-blue-500 text-xl"
              />
            {{ $t('Cart') }}
          </h2>
        </div>
        <div class="flex items-center gap-2">
          <ShareUrl
            v-if="cartShareUrl"
            :url="cartShareUrl"
            :title="$t('Share basket')" />
          <CloseButton
            data-testid="modalCloseIcon"
            @click="toggleCartSidebar"
          />
        </div>
      </div>
    </template>
    <CartSummary @share-url-ready="cartShareUrl = $event" />
    <template
      v-if="lineItems.length"
      #content-bottom
    >
      <div>
        <CartSidebarButtons
          :primary-button-text="$t('Checkout')"
          :total-price="total"
          :price-label="$t('Total price:')"
          @primary-button-click="handleClickCheckout"
        />
        <transition name="sf-fade">
          <div
            v-if="!isCartFrozen"
            class="mt-3 flex justify-end px-4 pb-2">
            <CustomButton
              type="raw"
              :aria-label="$t('Print cart as PDF')"
              :disabled="isGeneratingPdf"
              @click="handleOpenGenerateQuoteModal"
            >
              <span class="flex items-center gap-2">
                <i class="iconify svg--download-file text-EXPONDO-blue text-base" />
                <span class="text-EXPONDO-blue underline">
                  {{ isGeneratingPdf ? $t('Preparing PDF') : $t('Generate quote in PDF') }}
                </span>
                <InfoTag
                  :text="$t('New feature label')"
                  variant="success"
                  class="px-2 py-0.5 text-xs font-light leading-4"
                />
              </span>
            </CustomButton>
          </div>
        </transition>
      </div>
    </template>

    <CartGenerateQuoteModal
      :visible="isGenerateQuoteModalOpen"
      :loading="isGeneratingPdf"
      @close="handleCloseGenerateQuoteModal"
      @submit="handleGenerateQuote"
    />
  </SideBar>
</template>

<script lang="ts">

import { cartGetters, useUser } from '~/utils/ct';
import { Logger } from '~~/helpers/logger';
import {
  formatPrintableCurrency,
  getPrintableCartItems,
  getPrintableCartTotals,
} from '~~/helpers/cart/getPrintableCartData';
import { getTotalGrossPrice } from '~~/helpers/cart/getTotalPricesModule';
import type { CustomerInfo, PdfQuoteRequestBody } from '~~/types/helpers/printableCart';

export default defineComponent({
  name: 'CartSidebar',

  setup() {

    const { isCartSidebarOpen, toggleCartSidebar } = useCartSidebarStore();
    const { cart, isCartFrozen } = useCartExtended();
    const { handleGoToCheckout } = useCartSidebars();
    const { t, locale } = useI18n();
    const { currencyCode } = useLocalization();
    const {
      frozenCartLink,
      isLoadingCartSessionTransferToken,
      isFreezingCart,
      handleFreezeCart,
      ensureFrozenCartLinkForPdf,
    } = useFrozenCart();

    const { lineItems } = useLineItems();
    const cartShareUrl = ref<string>('');
    const isGeneratingPdf = ref(false);
    const isGenerateQuoteModalOpen = ref(false);
    const quoteFormData = ref<CustomerInfo | null>(null);

    const { isAuthenticated } = useUser();

    const totalItems = computed(() => cartGetters.getTotalItems(cart.value));
    const total = computed(() => getTotalGrossPrice(cart.value));

    const createPdfFileName = () => `cart-${new Date().toISOString().slice(0, 10)}.pdf`;

    const handleOpenGenerateQuoteModal = () => {

      if (!lineItems.value.length || isGeneratingPdf.value) {

        return;

      }

      isGenerateQuoteModalOpen.value = true;

    };

    const handleCloseGenerateQuoteModal = () => {

      if (isGeneratingPdf.value) {

        return;

      }

      isGenerateQuoteModalOpen.value = false;

    };

    const createQuotePayload = (): PdfQuoteRequestBody => {

      if (total.value === null) {

        Logger.error('Failed to generate quote PDF: cart total is not available yet.');

        throw new Error('Cart total is not available yet.');

      }

      const currentLocale = typeof locale.value === 'string' && locale.value.length ? locale.value : 'en';
      const printableItems = getPrintableCartItems({
        lineItems: lineItems.value,
        fallbackCurrencyCode: currencyCode.value,
        locale: currentLocale,
        fallbackItemName: t('Item').toString(),
      });
      const printableTotals = getPrintableCartTotals({
        lineItems: lineItems.value,
        fallbackCurrencyCode: currencyCode.value,
        locale: currentLocale,
      });

      const termsPricesText = t('All prices are in {currency} and include applicable VAT where required.', {
        currency: currencyCode.value,
      }).toString();

      // Resolved here, not as a static label — it's the quote's actual generation timestamp,
      // formatted for the customer's locale at the moment the quote is generated.
      const now = new Date();
      const createdAtLabel = t('pdf-quote.creation-timestamp', {
        creationDate: now.toLocaleDateString(currentLocale),
        creationTime: now.toLocaleTimeString(currentLocale),
      }).toString();

      return {
        locale: currentLocale,
        currency: currencyCode.value,
        total: formatPrintableCurrency({
          amount: total.value,
          currency: currencyCode.value,
          locale: currentLocale,
        }),
        netTotal: printableTotals.netTotal,
        vatTotal: printableTotals.vatTotal,
        items: printableItems,
        customerInfo: quoteFormData.value || undefined,
        frozenCartLink: frozenCartLink.value,
        labels: {
          quoteTitle: t('pdf-quote.document-title').toString(),
          title: t('Cart').toString(),
          createdAtLabel,
          itemsLabel: t('Items').toString(),
          itemLabel: t('Item').toString(),
          quantityLabel: t('Quantity').toString(),
          skuLabel: t('SKU').toString(),
          netPriceLabel: t('Net price').toString(),
          vatLabel: t('VAT').toString(),
          totalLabel: t('Total price').toString(),
          netTotalLabel: t('Net total price').toString(),
          vatTotalLabel: t('VAT total').toString(),
          grandTotalLabel: t('Total price (incl. VAT)').toString(),
          totalsNoteLabel: t('pdf-quote.all-totals-explanation').toString(),
          customerInformationLabel: t('Company').toString(),
          vatIdLabel: t('VAT ID').toString(),
          companyNameLabel: t('Company name').toString(),
          addressLabel: t('Street name and house number').toString(),
          termsAndConditionsLabel: t('pdf-quote.terms-and-conditions').toString(),
          termsValidityLabel: t('This quote is valid for 7 calendar days from the date of issue.').toString(),
          termsPricesLabel: termsPricesText,
          termsShippingLabel: t('Shipping is free on all quoted orders.').toString(),
          termsStockLabel: t(
            'Stock availability is not guaranteed until the order is finalised and payment is processed.',
          ).toString(),
          purchaseLinkLabel: t('Link to proceed with the purchase').toString(),
          purchaseLinkAnchorLabel: t('shopping cart').toString(),
          requiresAuthenticationLabel: isAuthenticated.value
            ? t('You will need to log in to your account to access this quote.').toString()
            : '',
          estimatedDeliveryLabel: t('Estimated Delivery Time').toString(),
          estimatedDeliveryTextLabel: t('pdf-quote.estimated-delivery-explanation').toString(),
          legalFooterCompanyLabel: t('pdf-quote.legal-footer-company').toString(),
          legalFooterDisclaimerLabel: t('pdf-quote.legal-footer-disclaimer').toString(),
        },
      };

    };

    const generateCartPdf = async () => {

      if (!import.meta.client || !lineItems.value.length || isGeneratingPdf.value) {

        return;

      }

      let isPdfGenerated = false;
      isGeneratingPdf.value = true;

      try {

        const fileName = createPdfFileName();
        const quotePayload = createQuotePayload();
        const response = await fetch('/internal-api/pdf/generate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...quotePayload,
            filename: fileName,
          }),
        });

        if (!response.ok) {

          const errorDetails = await response.text();

          throw new Error(
            errorDetails
              ? `PDF generation failed with status ${response.status}: ${errorDetails}`
              : `PDF generation failed with status ${response.status}`,
          );

        }

        const pdfBlob = await response.blob();
        const objectUrl = window.URL.createObjectURL(pdfBlob);
        const anchor = document.createElement('a');

        anchor.href = objectUrl;
        anchor.download = fileName;
        anchor.click();

        window.URL.revokeObjectURL(objectUrl);
        isPdfGenerated = true;

      } catch (error) {

        Logger.error('Failed to generate cart PDF: ', error);

      } finally {

        isGeneratingPdf.value = false;

      }

      return isPdfGenerated;

    };

    const handleGenerateQuote = async (formData: CustomerInfo) => {

      quoteFormData.value = formData;
      const isFrozenCartReady = await ensureFrozenCartLinkForPdf();

      if (!isFrozenCartReady) {

        Logger.error('Failed to generate quote PDF: ' +
        'cart freezing did not complete successfully. Check browser console for details.');

        return;

      }

      const isPdfGenerated = await generateCartPdf();

      if (isPdfGenerated) {

        isGenerateQuoteModalOpen.value = false;

      }

    };

    const handleClickCheckout = () => {

      toggleCartSidebar();
      handleGoToCheckout();

    };

    return {
      isCartSidebarOpen,
      toggleCartSidebar,
      lineItems,
      handleClickCheckout,
      handleOpenGenerateQuoteModal,
      handleCloseGenerateQuoteModal,
      handleGenerateQuote,
      isGeneratingPdf,
      isGenerateQuoteModalOpen,
      totalItems,
      total,
      cartShareUrl,
      cart,
      isLoadingCartSessionTransferToken,
      isFreezingCart,
      frozenCartLink,
      isCartFrozen,
      handleFreezeCart,
    };

  },
});

</script>
